<script setup lang="ts">
import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-vue-next'

import FieldRow from '../FieldRow.vue'
import ResetButton from '../ResetButton.vue'
import useGoogleFonts from '../../composables/googleFonts'
import { matchesFont, toBrandAppFont, type BrandAppFont, type GoogleFontItem } from '../../lib/brandAppFonts'

const PAGE_SIZE = 50
const SCROLL_THRESHOLD = 120
const ALL_CATEGORIES = 'all'
const CATEGORIES = ['sans-serif', 'serif', 'display', 'handwriting', 'monospace']

const props = defineProps<{
  id: string
  label: string
  modelValue?: BrandAppFont | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: BrandAppFont | null]
}>()

const { t } = useI18n()
const { catalog, status, load, loadPreview } = useGoogleFonts()

const isOpen = ref(false)
const search = ref('')
const category = ref(ALL_CATEGORIES)
const limit = ref(PAGE_SIZE)

const matches = computed(() => {
  const selectedCategory = category.value === ALL_CATEGORIES ? null : category.value

  return catalog.value.filter((item) => matchesFont(item, search.value, selectedCategory))
})

const results = computed(() => matches.value.slice(0, limit.value))

const listRef = useTemplateRef<HTMLElement>('listRef')

watch([search, category], () => {
  limit.value = PAGE_SIZE
  listRef.value?.scrollTo({ top: 0 })
})

const onListScroll = (event: Event) => {
  const list = event.target as HTMLElement
  if (list.scrollHeight - list.scrollTop - list.clientHeight > SCROLL_THRESHOLD) return
  if (limit.value < matches.value.length) limit.value += PAGE_SIZE
}

const fontStyle = (family: string) => ({ fontFamily: `'${family}', sans-serif` })

watch(results, (items) => {
  if (isOpen.value) items.forEach((item) => loadPreview(item.family))
})

watch(
  () => props.modelValue?.family,
  (family) => {
    if (family) loadPreview(family)
  },
  { immediate: true }
)

const onOpenChange = (open: boolean) => {
  isOpen.value = open
  if (!open) return
  load()
  results.value.forEach((item) => loadPreview(item.family))
}

const onCategoryChange = (value: unknown) => {
  category.value = typeof value === 'string' ? value : ALL_CATEGORIES
}

const select = (item: GoogleFontItem) => {
  const font = toBrandAppFont(item)
  if (!font) return
  emit('update:modelValue', font)
  isOpen.value = false
}

const reset = () => {
  emit('update:modelValue', null)
  isOpen.value = false
}
</script>

<template>
  <FieldRow :label="label" :label-for="id">
    <ResetButton
      v-if="modelValue"
      :label="t('pages.loyaltySupports.webapp.editor.design.typography.reset')"
      @click="reset"
    />
    <Popover :open="isOpen" @update:open="onOpenChange">
      <PopoverTrigger as-child>
        <Button :id="id" type="button" variant="outline" class="w-60 justify-between">
          <span class="truncate" :style="modelValue ? fontStyle(modelValue.family) : undefined">
            {{ modelValue?.family ?? t('pages.loyaltySupports.webapp.editor.design.typography.defaultFont') }}
          </span>
          <ChevronsUpDown class="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-80 p-0" align="end">
        <div class="flex gap-2 border-b p-2">
          <Input
            v-model="search"
            type="search"
            class="min-w-0 flex-1"
            :placeholder="t('pages.loyaltySupports.webapp.editor.design.typography.searchPlaceholder')"
            :aria-label="t('pages.loyaltySupports.webapp.editor.design.typography.searchPlaceholder')"
          />
          <Select :model-value="category" @update:model-value="onCategoryChange">
            <SelectTrigger
              class="w-32"
              :aria-label="t('pages.loyaltySupports.webapp.editor.design.typography.category')"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL_CATEGORIES">
                {{ t('pages.loyaltySupports.webapp.editor.design.typography.allCategories') }}
              </SelectItem>
              <SelectItem v-for="item in CATEGORIES" :key="item" :value="item">
                {{ t(`pages.loyaltySupports.webapp.editor.design.typography.categories.${item}`) }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div ref="listRef" class="flex max-h-72 flex-col overflow-y-auto p-1" role="listbox" @scroll="onListScroll">
          <Button
            variant="ghost"
            class="w-full justify-between"
            role="option"
            :aria-selected="!modelValue"
            @click="reset"
          >
            <span class="truncate">{{ t('pages.loyaltySupports.webapp.editor.design.typography.defaultFont') }}</span>
            <Check v-if="!modelValue" />
          </Button>

          <div
            v-if="status === 'loading' || status === 'idle'"
            class="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm"
          >
            <LoaderCircle class="size-4 animate-spin" />
            {{ t('pages.loyaltySupports.webapp.editor.design.typography.catalogLoading') }}
          </div>
          <p v-else-if="status === 'error'" class="text-muted-foreground px-3 py-2 text-sm">
            {{ t('pages.loyaltySupports.webapp.editor.design.typography.catalogError') }}
          </p>
          <template v-else>
            <Button
              v-for="item in results"
              :key="item.family"
              variant="ghost"
              class="w-full justify-between"
              role="option"
              :aria-selected="modelValue?.family === item.family"
              @click="select(item)"
            >
              <span class="truncate text-base" :style="fontStyle(item.family)">{{ item.family }}</span>
              <Check v-if="modelValue?.family === item.family" />
            </Button>
            <p v-if="results.length === 0" class="text-muted-foreground px-3 py-2 text-sm">
              {{ t('pages.loyaltySupports.webapp.editor.design.typography.noResults') }}
            </p>
          </template>
        </div>
      </PopoverContent>
    </Popover>
  </FieldRow>
</template>
